/**
 * Shared chrome atoms for the input-request recipient cards (Batch IR-2a).
 *
 * Small presentational pieces reused across the three card registers (OS owner,
 * task, general): the Eric avatar, a relative "sent {when}" formatter, and a
 * tag pill. Kept here so each card wrapper stays focused on its register's
 * layout. All copy that appears here is fixed chrome; operator-authored content
 * never flows through these atoms.
 */

/**
 * The navy circular Eric avatar (an italic serif "E"), matching the design
 * prototypes and the OS owner surface (OsStatusComponent's EricAvatar).
 */
export function EricAvatar({ size = 28 }: { size?: number }): JSX.Element {
  return (
    <div
      className="flex shrink-0 items-center justify-center rounded-full bg-[color:var(--color-navy)]"
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <span
        className="font-display italic text-[color:var(--color-linen)]"
        style={{ fontSize: size * 0.42 }}
      >
        E
      </span>
    </div>
  );
}

type TagVariant = 'rose' | 'linen' | 'green';

/**
 * A small uppercase tag pill (the design `Tag` atom), used for the task/general
 * card state markers ("Needs you" / "Answered" / "Closed").
 */
export function Tag({
  children,
  variant = 'linen',
}: {
  children: string;
  variant?: TagVariant;
}): JSX.Element {
  const styles: Record<TagVariant, string> = {
    rose: 'bg-[color:var(--color-rose)] text-[color:var(--color-navy)] border-transparent',
    linen:
      'bg-[color:var(--color-linen)] text-[color:var(--color-black)] border-[color:var(--color-line-dark)]',
    green:
      'bg-[color:var(--color-green-accent-subtle)] text-[color:var(--color-green-accent)] border-[color:var(--color-green-accent)]',
  };
  return (
    <span
      className={[
        'inline-flex shrink-0 items-center whitespace-nowrap rounded-[2px] border px-2 py-0.5 text-[10px] font-medium uppercase leading-none tracking-[0.05em]',
        styles[variant],
      ].join(' ')}
    >
      {children}
    </span>
  );
}

/**
 * The green check banner shown atop a submitted card. The trailing line varies
 * by register (the task card appends "· nothing more to do" and a tag).
 */
export function SentBanner({
  trailing,
  tag,
}: {
  trailing: string;
  tag?: JSX.Element;
}): JSX.Element {
  return (
    <div className="flex items-center gap-2.5 rounded-md border border-[color:var(--color-green-accent)] bg-[color:var(--color-green-accent-subtle)] px-3.5 py-2.5">
      <span
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[color:var(--color-green-accent)] text-[11px] text-[color:var(--color-white)]"
        aria-hidden="true"
      >
        &#10003;
      </span>
      <span className="text-[13px] font-semibold text-[color:var(--color-green-accent)]">
        Sent to Eric
      </span>
      <span className="ml-auto text-[12px] text-[color:var(--color-text-mute)]">
        {trailing}
      </span>
      {tag ?? null}
    </div>
  );
}
